import { useFormNotification, useTranslate } from "@commonlibs";
import { PointsBalanceFieldProps } from "../PointsBalanceField";
import { NumericTextFieldProps } from "ui/common/components/input/NumericTextField/NumericTextField.types";
import usePointFindBy from "features/points/hooks/usePointFindBy";
import { formatTokenAmount, parseTokenAmount } from "features/wallet/utils/tokenAmount";

export interface UsePointsBalanceValidation extends Pick<PointsBalanceFieldProps, "validators" | "error" | "name"> {
    value: string;
}

export type UsePointsBalanceValidationResult = Pick<NumericTextFieldProps, "error">

function getPointsBalanceErrors(translate: unknown, decimals: unknown, bigIntValue: string, validatorType: string, validator?: string) {
    let condition = Boolean(validator) && BigInt(bigIntValue) <= BigInt(validator!);

    if (validatorType === "gte") {
        condition = Boolean(validator) && BigInt(bigIntValue) < BigInt(validator!);
    } else if (validatorType === "lte") {
        condition = Boolean(validator) && BigInt(bigIntValue) > BigInt(validator!);
    } else if (validatorType === "gt") {
        condition = Boolean(validator) && BigInt(bigIntValue) <= BigInt(validator!);
    }

    const isError = condition;
    // @ts-ignore - avoid breaking the application
    const formattedBalance = formatTokenAmount(validator || "0", decimals || 0);
    // @ts-ignore - avoid breaking the application
    const error: [boolean, string] = [isError, translate(`invalid_number_${validatorType}`, { ns: "error", n: formattedBalance })];

    return [isError, error];
}

export default function usePointsBalanceValidation({
    validators: { gt, gte, lte } = {},
    error,
    value,
    name,
}: UsePointsBalanceValidation): UsePointsBalanceValidationResult {
    const translate = useTranslate();
    const { data: { decimals } = {} } = usePointFindBy();

    const bigIntValue = parseTokenAmount(value || "0", decimals || 0);

    const [isGtError, gtError] = getPointsBalanceErrors(translate, decimals, bigIntValue, "gt", gt);
    const [isGteError, gteError] = getPointsBalanceErrors(translate, decimals, bigIntValue, "gte", gte);
    const [isLteError, lteError] = getPointsBalanceErrors(translate, decimals, bigIntValue, "lte", lte);

    const isErrorProp = (Array.isArray(error) && error[0]) || (typeof error === "boolean" && error);
    const isError = isGtError || isGteError || isLteError || isErrorProp;

    useFormNotification(name, bigIntValue, !isError);

    return {
        ...{
            ...(isGtError && { error: gtError }),
            ...(isGteError && { error: gteError }),
            ...(isLteError && { error: lteError }),
            ...(isErrorProp && { error }),
        },
    };
}